import React, { useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import UserContext from 'context/UserContext';
import useLang from 'context/LanguageContext';
import { translate as t } from 'utils/helpers';
import styles from './Popup.module.scss';

const PlanLimitExceedPopup = () => {
  const { lang } = useLang();
  const [userData, setUserData] = useContext(UserContext);

  // Function to format the trial message with an unordered list
  const formatTrialMessage = (message) => {
    if (!message) return null;

    // Split the message by newlines
    const parts = message.split('\n\n');

    // Find the part that starts with the podcast growth section (works for both English and Spanish)
    const growthSectionIndex = parts.findIndex(part => 
      part.includes('🎙️') && 
      (part.includes('Keep growing your podcast with:') || part.includes('Sigue creciendo tu pódcast con:'))
    );

    if (growthSectionIndex === -1) {
      // If the growth section is not found, return the message as is
      return <p className={styles.modalDescription} style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{message}</p>;
    }

    // Extract the list items (they come after the growth section)
    const listItemsSection = parts.slice(growthSectionIndex + 1, parts.length - 1);

    // The last part contains "...and much more." and the final message
    const finalPart = parts[parts.length - 1];

    // Create the JSX elements
    return (
      <div style={{ textAlign: 'left' }}>
        {/* Render the parts before the list */}
        {parts.slice(0, growthSectionIndex + 1).map((part, index) => (
          <p key={`pre-${index}`} className={styles.modalDescription} style={{ whiteSpace: 'pre-line' }}>
            {part}
          </p>
        ))}

        {/* Render the list items as an unordered list */}
        <ul className={styles.modalDescription}>
          {listItemsSection.map((item, index) => (
            <li key={`item-${index}`}>{item.trim()}</li>
          ))}
        </ul>

        {/* Render the final part */}
        <p className={styles.modalDescription} style={{ whiteSpace: 'pre-line' }}>
          {finalPart}
        </p>
      </div>
    );
  };

  const hideModal = () => {
    if (userData?.planLimitPopup?.closeActionCallback) {
      userData?.planLimitPopup?.closeActionCallback();
    }
    setUserData((prevState) => ({
      ...prevState,
      planLimitPopup: { showModal: false },
    }));
  };

  return (
    <Modal
      className={styles.modalParent}
      show={userData?.planLimitPopup?.showModal}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      onHide={hideModal}>
      <Modal.Body className={styles.modalBody}>
        <h4 className={styles.modalTitle}>{userData?.planLimitPopup?.title}</h4>
        {userData?.planLimitPopup?.message && formatTrialMessage(userData?.planLimitPopup?.message)}
        {userData?.planLimitPopup?.planLimitExceedName === 'Free Plan' && (
          <>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )('congratulationsYouHaveReachedTheLimitsOfTheFreePlan')}
            </p>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )(
                'subscribeNowToAPaidPlanToKeepGrowingAndEnjoyAllOurAdvancedFeatures',
              )}
            </p>
          </>
        )}
        {userData?.planLimitPopup?.planLimitExceedName === 'OLD_PRO_PLAN' && (
          <>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )('congratulationsYouHaveReachedTheLimitsOfTheProPlan')}
            </p>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )(
                'subscribeNowToThePLusPlanToKeepGrowingAndEnjoyAllOurAdvancedFeatures',
              )}
            </p>
          </>
        )}
        {userData?.planLimitPopup?.planLimitExceedName === 'BASIC' && (
          <>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )('congratulationsYouHaveReachedTheLimitsOfTheBasicPlan')}
            </p>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )(
                'subscribeNowToTheProPlanToKeepGrowingAndEnjoyAllOurAdvancedFeatures',
              )}
            </p>
          </>
        )}
        {userData?.planLimitPopup?.planLimitExceedName === 'PRO' && (
          <>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )('congratulationsYouHaveReachedTheLimitsOfTheProPlan')}
            </p>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )(
                'subscribeNowToThePLusPlanToKeepGrowingAndEnjoyAllOurAdvancedFeatures',
              )}
            </p>
          </>
        )}
        {userData?.planLimitPopup?.planLimitExceedName === 'PLUS' && (
          <>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(
                lang,
                'settings',
              )('congratulationsYouHaveReachedTheLimitsOfThePlusPlan')}
            </p>
            <p
              className={styles.modalDescription}
              style={{ whiteSpace: 'pre-line' }}>
              {t(lang, 'settings')('contactUsToLearnMoreAboutOurUltraPlan')}
            </p>
          </>
        )}
        <div className={styles.confirmCTAs}>
          {userData?.planLimitPopup?.showCancelButton !== false && (
            <Button
              className={`${styles.popupBtn} compact mb-3`}
              variant="outline-primary"
              onClick={hideModal}>
              {t(lang, 'common')('close')}
            </Button>
          )}
          {userData?.planLimitPopup?.showSubmitButton && (
            <Button
              className={`${styles.popupBtn} compact mb-3`}
              onClick={() => {
                hideModal();
                userData?.planLimitPopup?.confirmActionCallback();
              }}
              style={{
                backgroundColor: userData?.planLimitPopup?.submitButtonColor,
                color: userData?.planLimitPopup?.submitButtonColor && '#fff',
                borderColor: userData?.planLimitPopup?.submitButtonColor,
              }}>
              {userData?.planLimitPopup?.submitButtonText
                ? userData?.planLimitPopup?.submitButtonText
                : userData?.planLimitPopup?.planLimitExceedName === 'PLUS'
                  ? t(lang, 'settings')('contact')
                  : t(lang, 'settings')('subscribeNow')}
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PlanLimitExceedPopup;
